<script setup lang="ts">
import { ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from '@/i18n'

const { t } = useI18n()

interface Scene {
  id: number
  idx: number
  start_time: number
  duration_s: number
  description: string | null
  image_prompt: string | null
  video_prompt: string | null
  status: string
  image_url: string | null
}

const props = defineProps<{
  projectId: number
  scenes: Scene[]
  projectStatus: string
  projectMode: string
  regenerationsRemaining: number
}>()

const emit = defineEmits<{
  (e: 'validate'): void
  (e: 'regenerate', sceneId: number, newPrompt: string): void
  (e: 'upload', sceneId: number, file: File): void
  (e: 'refresh'): void
}>()

const selectedScene = ref<Scene | null>(null)
const showDialog = ref(false)
const editedPrompt = ref('')
const uploading = ref(false)
const regenerating = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

const canEdit = computed(() => 
  props.projectStatus === 'images_ready' && props.projectMode === 'manual'
)

const canRegenerate = computed(() => props.regenerationsRemaining > 0)

function openSceneDialog(scene: Scene) {
  selectedScene.value = scene
  editedPrompt.value = scene.image_prompt || ''
  showDialog.value = true
}

function closeDialog() {
  showDialog.value = false
  selectedScene.value = null
  editedPrompt.value = ''
}

async function handleRegenerate() {
  if (!selectedScene.value) return
  
  if (!canRegenerate.value) {
    ElMessage.warning(t('images.regeneration_limit_reached'))
    return
  }
  
  // Validate that the prompt is not empty
  if (!editedPrompt.value || editedPrompt.value.trim() === '') {
    ElMessage.error(t('images.prompt_required'))
    return
  }
  
  try {
    await ElMessageBox.confirm(
      t('images.confirm_regenerate_message', { remaining: props.regenerationsRemaining }),
      t('images.confirm_regenerate_title'),
      {
        confirmButtonText: t('images.regenerate'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )
    
    regenerating.value = true
    // Always send the edited prompt (the English prompt displayed in the editable field)
    // This ensures the user's visible prompt is always used for regeneration
    emit('regenerate', selectedScene.value.id, editedPrompt.value.trim())
    closeDialog()
    ElMessage.success(t('images.regeneration_started'))
  } catch {
    // Cancelled
  } finally {
    regenerating.value = false
  }
}

function triggerUpload() {
  fileInput.value?.click()
}

async function handleFileUpload(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (!file || !selectedScene.value) return
  
  // Validate file type
  if (!file.type.startsWith('image/')) {
    ElMessage.error(t('images.invalid_file_type'))
    return
  }
  
  // Validate file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    ElMessage.error(t('images.file_too_large'))
    return
  }
  
  uploading.value = true
  try {
    emit('upload', selectedScene.value.id, file)
    closeDialog()
    ElMessage.success(t('images.upload_success'))
  } catch (error) {
    ElMessage.error(t('images.upload_error'))
  } finally {
    uploading.value = false
    // Reset file input
    if (target) target.value = ''
  }
}

async function validateAllImages() {
  try {
    await ElMessageBox.confirm(
      t('images.confirm_validate_message'),
      t('images.confirm_validate_title'),
      {
        confirmButtonText: t('images.validate_and_generate'),
        cancelButtonText: t('common.cancel'),
        type: 'info'
      }
    )
    emit('validate')
  } catch {
    // Cancelled
  }
}

function formatDuration(seconds: number): string {
  return `${seconds.toFixed(1)}s`
}
</script>

<template>
  <div class="image-validator">
    <!-- Header -->
    <div class="validator-header">
      <div class="header-info">
        <h2>{{ t('images.title') }}</h2>
        <div class="header-stats">
          <span class="stat">
            <strong>{{ scenes.length }}</strong> {{ t('images.images') }}
          </span>
          <span class="stat regenerations" :class="{ 'warning': regenerationsRemaining <= 1 }">
            <strong>{{ regenerationsRemaining }}</strong> {{ t('images.regenerations_left') }}
          </span>
        </div>
      </div>
      
      <div v-if="canEdit" class="header-actions">
        <el-button @click="validateAllImages" type="success" size="large">
          {{ t('images.validate_and_generate') }}
        </el-button>
      </div>
    </div>

    <!-- Info Alert -->
    <el-alert 
      type="info" 
      :title="t('images.instructions')"
      show-icon
      :closable="false"
      class="instructions-alert"
    />

    <!-- Images Grid -->
    <div class="images-grid">
      <div 
        v-for="scene in scenes" 
        :key="scene.id"
        class="image-card"
        :class="{ 'clickable': canEdit }"
        @click="canEdit && openSceneDialog(scene)"
      >
        <div class="image-container">
          <img 
            v-if="scene.image_url" 
            :src="scene.image_url" 
            :alt="`Scene ${scene.idx + 1}`"
            class="scene-image"
          />
          <div v-else class="image-placeholder">
            <span>{{ scene.idx + 1 }}</span>
          </div>
          <div class="image-overlay">
            <span class="edit-hint">{{ t('images.click_to_edit') }}</span>
          </div>
        </div>
        <div class="image-info">
          <div class="image-header">
            <span class="scene-number">{{ t('script.description') }} {{ scene.idx + 1 }}</span>
            <span class="scene-duration">{{ formatDuration(scene.duration_s) }}</span>
          </div>
          <p class="image-description">{{ scene.description || '-' }}</p>
        </div>
      </div>
    </div>

    <!-- Scene Edit Dialog -->
    <el-dialog
      v-model="showDialog"
      :title="t('images.edit_image')"
      width="800px"
      destroy-on-close
      class="image-edit-dialog"
    >
      <div v-if="selectedScene" class="dialog-content">
        <!-- Large Image Preview -->
        <div class="image-preview-large">
          <img 
            v-if="selectedScene.image_url" 
            :src="selectedScene.image_url" 
            :alt="`Scene ${selectedScene.idx + 1}`"
          />
          <div v-else class="preview-placeholder">
            {{ t('images.no_image') }}
          </div>
        </div>

        <!-- Scene Info -->
        <div class="scene-details">
          <div class="detail-item">
            <label>{{ t('script.description') }}</label>
            <p>{{ selectedScene.description || '-' }}</p>
          </div>

          <!-- Editable Prompt -->
          <div class="detail-item">
            <label>{{ t('script.image_prompt') }}</label>
            <el-input 
              v-model="editedPrompt" 
              type="textarea"
              :rows="4"
              :placeholder="t('script.image_prompt_placeholder')"
            />
          </div>
        </div>

        <!-- Actions -->
        <div class="dialog-actions">
          <div class="action-group">
            <el-button 
              @click="handleRegenerate" 
              type="primary"
              :disabled="!canRegenerate"
              :loading="regenerating"
            >
              <span class="action-icon">🔄</span>
              {{ t('images.regenerate') }}
              <span v-if="canRegenerate" class="action-hint">
                ({{ regenerationsRemaining }} {{ t('images.remaining') }})
              </span>
            </el-button>
            <p v-if="!canRegenerate" class="limit-warning">
              {{ t('images.regeneration_limit_reached') }}
            </p>
          </div>

          <div class="action-group">
            <el-button @click="triggerUpload" type="success" :loading="uploading">
              <span class="action-icon">📤</span>
              {{ t('images.upload_replacement') }}
            </el-button>
            <p class="upload-hint">{{ t('images.upload_hint') }}</p>
            <input 
              ref="fileInput"
              type="file" 
              accept="image/*"
              style="display: none"
              @change="handleFileUpload"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <el-button @click="closeDialog">{{ t('common.close') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.image-validator {
  background: var(--m2v-bg-surface);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-xl);
  padding: var(--m2v-space-6);
}

.validator-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--m2v-space-4);
  padding-bottom: var(--m2v-space-4);
  border-bottom: 1px solid var(--m2v-border-subtle);
}

.validator-header h2 {
  margin: 0 0 var(--m2v-space-2) 0;
  font-size: 1.5rem;
}

.header-stats {
  display: flex;
  gap: var(--m2v-space-4);
  color: var(--m2v-text-muted);
}

.stat strong {
  color: var(--m2v-text-primary);
}

.regenerations.warning strong {
  color: var(--el-color-warning);
}

.instructions-alert {
  margin-bottom: var(--m2v-space-6);
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--m2v-space-4);
}

.image-card {
  background: var(--m2v-bg-elevated);
  border: 1px solid var(--m2v-border-subtle);
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
  transition: all var(--m2v-transition-fast);
}

.image-card.clickable {
  cursor: pointer;
}

.image-card.clickable:hover {
  border-color: var(--m2v-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-card.clickable:hover .image-overlay {
  opacity: 1;
}

.image-container {
  position: relative;
  aspect-ratio: 16/9;
  overflow: hidden;
}

.scene-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--m2v-bg-base);
  font-size: 2rem;
  color: var(--m2v-text-muted);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--m2v-transition-fast);
}

.edit-hint {
  color: white;
  font-weight: 600;
  padding: var(--m2v-space-2) var(--m2v-space-4);
  background: var(--m2v-primary);
  border-radius: var(--m2v-radius-md);
}

.image-info {
  padding: var(--m2v-space-3);
}

.image-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--m2v-space-2);
}

.scene-number {
  font-weight: 600;
  font-size: 0.875rem;
}

.scene-duration {
  font-size: 0.75rem;
  background: var(--m2v-bg-surface);
  padding: 2px 8px;
  border-radius: var(--m2v-radius-sm);
}

.image-description {
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Dialog Styles */
.dialog-content {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-6);
}

.image-preview-large {
  width: 100%;
  max-height: 400px;
  border-radius: var(--m2v-radius-lg);
  overflow: hidden;
  background: var(--m2v-bg-base);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview-large img {
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
}

.preview-placeholder {
  padding: var(--m2v-space-12);
  color: var(--m2v-text-muted);
}

.scene-details {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-4);
}

.detail-item label {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--m2v-text-secondary);
  margin-bottom: var(--m2v-space-2);
}

.detail-item p {
  margin: 0;
  color: var(--m2v-text-primary);
}

.dialog-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--m2v-space-6);
  padding-top: var(--m2v-space-4);
  border-top: 1px solid var(--m2v-border-subtle);
}

.action-group {
  display: flex;
  flex-direction: column;
  gap: var(--m2v-space-2);
}

.action-icon {
  margin-right: var(--m2v-space-2);
}

.action-hint {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-left: var(--m2v-space-1);
}

.limit-warning {
  margin: 0;
  font-size: 0.75rem;
  color: var(--el-color-danger);
}

.upload-hint {
  margin: 0;
  font-size: 0.75rem;
  color: var(--m2v-text-muted);
}

@media (max-width: 768px) {
  .validator-header {
    flex-direction: column;
    gap: var(--m2v-space-4);
  }
  
  .images-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
  
  .dialog-actions {
    grid-template-columns: 1fr;
  }
}
</style>
